import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import ManagementHeaderTabs from '../Management/ManagementHeaderTabs';
import OperationsProgressionTab from './OperationsProgressionTab';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  acceptRejectApproval,
  getEngineerPrioritiesByProjectId,
  getEngineerPrioritiesByDate,
  getEngineerPendingApprovals,
  getCustomerChangeRequests,
  getOperationsOnboardingMap,
  updateTaskForTomorrow
} from '../../services/api/implementation/impl';
import { useNavigate, useLocation, NavLink, useSearchParams } from 'react-router-dom';
import Button from '../../../shared/components/ui/Button';
import Modal from '../../../shared/components/ui/Modal';
import toast from 'react-hot-toast';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import Loader from '../../../shared/components/ui/Loader';
import EngGraphs from './EngGraphs';
import AiPulsePanel from '../../components/ai/AiPulsePanel';
import DashboardFilters from '../../../shared/components/ui/DashboardFilters';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import {
  getOperationsPriorityNavigation,
  getPendingOperationsDailyTasks,
  isOperationsDashboardPriorityType,
  shouldAutoCompleteOperationsPriorityOnRedirect
} from '../../utils/operationsDailyTaskTypes';
import { isDominoTask } from '../../utils/dailyTaskTypes';
import {
  buildOperationsHeaderTabParams,
  getOperationsHeaderTab
} from './operationsDashboardUrlParams';
import { isDominoEnabled } from '../../../shared/utils/authStorage';
import {
  buildReturnToFromLocation,
  navigateWithReturn,
  useSmartBack,
  shouldShowModuleHomeBack
} from '../../../shared/navigation';

const EngDashboard = () => {
  const { collapsed } = useSidebar();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const userVertical = Cookies.get('userVertical');
  const isManagementVertical = userVertical?.toLowerCase() === 'management';
  const showProgressionTab = !isManagementVertical && isDominoEnabled();
  const headerTab = showProgressionTab ? getOperationsHeaderTab(searchParams) : 'dashboard';
  const isDashboardTab = headerTab === 'dashboard';
  const [selectedApproval, setSelectedApproval] = useState();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { onBack } = useSmartBack({ fallback: '/builder/dashboard' });
  const showHomeBack = shouldShowModuleHomeBack('operations', location);
  const userID = Number(Cookies.get('userId'));
  const [selectedDate, setSelectedDate] = useState(
    localStorage.getItem('engineerSelectedDate') || new Date().toISOString().split('T')[0]
  );
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [filters, setFilters] = useState({
    projectID: location.state?.projectID || null,
    month: location.state?.month || null
  });
  const [isFiltersLoading, setIsFiltersLoading] = useState(false);
  const [showAllPriorities, setShowAllPriorities] = useState(false);
  const [showAllApprovals, setShowAllApprovals] = useState(false);
  const [showAllChangeRequests, setShowAllChangeRequests] = useState(false);
  const { projectsForDropdown } = useProjectsList();
  const [projectPriorityCountsById, setProjectPriorityCountsById] = useState(null);
  const hasProjectSelected =
    filters.projectID !== null &&
    filters.projectID !== undefined &&
    String(filters.projectID) !== '';
  const resolvedProjectId = hasProjectSelected ? Number(filters.projectID) : null;

  const { data: onboardingResponse } = useQuery({
    queryKey: ['operationsOnboardingMap', resolvedProjectId, userID],
    queryFn: () => getOperationsOnboardingMap(resolvedProjectId, userID),
    enabled: showProgressionTab && !!resolvedProjectId && !!userID
  });

  const progressionPendingCount = useMemo(() => {
    const progress = onboardingResponse?.data?.progress;
    if (progress?.total != null && progress?.completed != null) {
      return Math.max(0, progress.total - progress.completed);
    }

    return (onboardingResponse?.data?.steps || []).filter(
      (step) => step.status !== 'completed'
    ).length;
  }, [onboardingResponse]);

  const handleHeaderTabChange = useCallback(
    (tab) => {
      setSearchParams(buildOperationsHeaderTabParams(searchParams, tab), { replace: true });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    if (!showProgressionTab && getOperationsHeaderTab(searchParams) === 'progression') {
      setSearchParams(buildOperationsHeaderTabParams(searchParams, 'dashboard'), { replace: true });
    }
  }, [showProgressionTab, searchParams, setSearchParams]);

  const {
    data: approvals,
    refetch: refetApprovals,
    isLoading: isLoadingApprovals
  } = useQuery({
    queryKey: ['allapprovals', filters.projectID, selectedDate],
    queryFn: () => getEngineerPendingApprovals(filters.projectID, selectedDate),
    enabled: isDashboardTab && !!filters.projectID && !!selectedDate
  });

  const {
    data: priorities,
    refetch: refetchPriorities,
    isLoading: isLoadingPriorities
  } = useQuery({
    queryKey: ['priorities', filters.projectID, selectedDate],
    queryFn: () => getEngineerPrioritiesByProjectId(filters.projectID, selectedDate),
    enabled: isDashboardTab && !!filters.projectID && !!selectedDate,
    staleTime: 0, // Data is considered stale immediately
    gcTime: 0, // Don't cache the data
    refetchOnMount: true, // Always refetch when component mounts
    refetchOnWindowFocus: false, // Don't refetch on window focus
    onSuccess: (data) => {
      mixpanel.track('Follow Ups API Success', {
        user_id: userID,
        project_id: filters.projectID,
        follow_ups_count: data?.data?.length || 0,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      mixpanel.track('Follow Ups API Failed', {
        user_id: userID,
        project_id: filters.projectID,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const {
    data: changeRequests,
    isLoading: isLoadingChangeRequests
  } = useQuery({
    queryKey: ['engineerChangeRequests', filters.projectID],
    queryFn: () => getCustomerChangeRequests(1, 1000, filters.projectID),
    enabled: isDashboardTab && !!filters.projectID,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true
  });

  const handleDateChange = (date) => {
    setSelectedDate(date);
    // Only save to localStorage when user manually changes the date
    localStorage.setItem('engineerSelectedDate', date);
    setShowDatePicker(false);

    // Track date change
    mixpanel.track('Dashboard Date Changed', {
      user_id: userID,
      selected_date: date,
      project_id: filters.projectID,
      timestamp: new Date().toISOString()
    });
  };

  // Close date picker when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDatePicker && !event.target.closest('.date-picker-container')) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  // Fetch "priorities count" once for the selected date, then group by project_id.
  React.useEffect(() => {
    let cancelled = false;

    const fetchCounts = async () => {
      if (!selectedDate) return;
      if (!Array.isArray(projectsForDropdown) || projectsForDropdown.length === 0) return;

      setProjectPriorityCountsById(null);

      const results = {};
      const itemsRes = await getEngineerPrioritiesByDate(selectedDate);
      // API response shape varies; handle the common wrappers.
      const items =
        Array.isArray(itemsRes)
          ? itemsRes
          : Array.isArray(itemsRes?.data)
            ? itemsRes.data
            : Array.isArray(itemsRes?.data?.data)
              ? itemsRes.data.data
              : Array.isArray(itemsRes?.data?.items)
                ? itemsRes.data.items
                : [];

      if (Array.isArray(items)) {
        items.forEach((item) => {
          if (item?.status === 'Completed') return;
          if (isDominoTask(item)) return;

          // dailyTask objects sometimes represent project id in different shapes.
          const pid =
            item?.project_id ??
            item?.projectId ??
            item?.project?.id ??
            item?.project?.project_id ??
            item?.liveTask?.project_id ??
            item?.liveTask?.projectId ??
            item?.liveTask?.project?.id ??
            item?.liveTask?.project?.project_id ??
            item?.liveTask?.unit?.project_id ??
            item?.liveTask?.unit?.projectId;

          if (pid === null || pid === undefined || pid === '') return;
          const key = String(pid);
          results[key] = (results[key] || 0) + 1;
        });
      }

      // Ensure "missing" projects show `0` instead of not showing counts.
      const fullResults = {};
      projectsForDropdown.forEach((p) => {
        if (!p?.id && p?.id !== 0) return;
        fullResults[String(p.id)] = results[String(p.id)] || 0;
      });

      if (!cancelled) setProjectPriorityCountsById(fullResults);
    };

    fetchCounts().catch(() => {
      if (!cancelled) setProjectPriorityCountsById({});
    });
    return () => {
      cancelled = true;
    };
  }, [projectsForDropdown, selectedDate]);

  const processPrioritiesData = (data) => {
    if (!data || !Array.isArray(data)) return [];

    const currentUserId = userID;
    const mine = [];
    const others = {};

    // Find current user's name from the data
    const currentUserItem = data.find((item) => item.user_id === currentUserId);
    const currentUserName = currentUserItem?.user?.name || `User ${currentUserId}`;

    data.forEach((item) => {
      // Skip completed and domino setup tasks (shown in Progression tab)
      if (item.status === 'Completed') return;
      if (isDominoTask(item)) return;

      const isMine = item.user_id === currentUserId;
      const userName = item.user?.name || `User ${item.user_id}`;

      if (isMine) {
        let taskType;
        if (item.type === 'Kickstart') {
          taskType = 'Kickstart';
        } else if (item.type === 'Posession Date') {
          taskType = 'Possession Date';
        } else if (item.type === 'Employees Allocation') {
          taskType = 'Employees Allocation';
        } else if (isOperationsDashboardPriorityType(item.type)) {
          taskType = item.type;
        } else if (item.isReview) {
          taskType = 'Review';
        } else if (item.isEscalated) {
          taskType = 'Escalated';
        } else {
          taskType = 'Updates';
        }

        const existingType = mine.find((task) => task.taskType === taskType);

        if (existingType) {
          existingType.count++;
        } else {
          mine.push({
            taskType,
            count: 1,
            userName: currentUserName
          });
        }
      } else {
        // Group by user for others - only include users with escalated tasks
        if (item.isEscalated) {
          const userId = item.user_id;
          if (!others[userId]) {
            others[userId] = {
              userId,
              userName: userName,
              escalatedCount: 0,
              totalCount: 0
            };
          }

          others[userId].escalatedCount++;
          others[userId].totalCount++;
        }
      }
    });

    const displayData = [];

    // Add Mine section if there are any
    if (mine.length > 0) {
      displayData.push({
        type: 'header',
        title: 'Mine'
      });

      mine.forEach((task) => {
        displayData.push({
          type: 'mine',
          taskType: task.taskType,
          count: task.count,
          userName: task.userName,
          userId: currentUserId
        });
      });
    }

    // Add Others section if there are any escalated tasks
    const othersArray = Object.values(others).filter((user) => user.escalatedCount > 0);
    if (othersArray.length > 0) {
      displayData.push({
        type: 'header',
        title: 'Others'
      });

      othersArray.forEach((user) => {
        displayData.push({
          type: 'others',
          userName: user.userName,
          escalatedCount: user.escalatedCount,
          totalCount: user.totalCount,
          userId: user.userId
        });
      });
    }

    return displayData;
  };

  const handleApproval = () => {
    const payload = {
      approved: true
    };

    // Track leave acceptance
    mixpanel.track('Approval Accepted', {
      user_id: userID,
      activity_id: selectedApproval,
      timestamp: new Date().toISOString()
    });

    handleAcceptRejectApproval({ payload, type: 'Approved' });
  };

  const { mutate: handleAcceptRejectApproval } = useMutation({
    mutationFn: ({ payload }) => acceptRejectApproval(payload, selectedApproval),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts

      mixpanel.track('Approval Process Started', {
        user_id: userID,
        activity_id: selectedApproval,
        timestamp: new Date().toISOString()
      });
    },
    onSuccess: async (result) => {
      setIsSubmitting(false);

      // Check if result is an error object (API now returns errors instead of throwing)
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        // Handle as error
        console.error('Error accepting/rejecting approvals', result);
        toast.error(
          result?.response?.data?.message ||
            result?.message ||
            'Error accepting/rejecting approvals'
        );

        mixpanel.track('Approval Process Failed', {
          user_id: userID,
          activity_id: selectedApproval,
          error_message: result?.response?.data?.message || result?.message || 'Unknown error',
          timestamp: new Date().toISOString()
        });
        return;
      }

      // Handle as success
      const type = result?.type || 'processed';
      toast.success(`Approval ${type} Successfully`);
      setShowConfirmationModal(false);

      mixpanel.track('Approval Process Success', {
        user_id: userID,
        activity_id: selectedApproval,
        action_type: type,
        timestamp: new Date().toISOString()
      });

      await refetApprovals();
      await refetchPriorities();
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error accepting/rejecting approvals', err);
      toast.error(
        err?.response?.data?.message || err?.message || 'Error accepting/rejecting approvals'
      );

      mixpanel.track('Approval Process Failed', {
        user_id: userID,
        activity_id: selectedApproval,
        error_message: err?.response?.data?.message || err?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const handleFiltersChange = ({ projectID, month }) => {
    setFilters({ projectID, month });
  };

  const handleFiltersLoadingChange = (isLoading) => {
    setIsFiltersLoading(isLoading);
  };

  return (
    <Layout>
      <Loader
        isVisible={
          isSubmitting ||
          isFiltersLoading ||
          (isDashboardTab &&
            (isLoadingApprovals || isLoadingPriorities || isLoadingChangeRequests))
        }
      />
      {showConfirmationModal && (
        <Modal
          isDirty
          confirmOnClose
          width={70}
          name={`Approval Details of '${approvals.data.find((item) => item.id === selectedApproval)?.task?.name}'`}
          onClose={() => {
            // Track modal close
            mixpanel.track('Approval Confirmation Modal Closed', {
              user_id: userID,
              activity_id: selectedApproval,
              timestamp: new Date().toISOString()
            });

            setShowConfirmationModal(false);
            setSelectedApproval();
          }}>
          {/* Header */}
          <div className="w-full mb-6">
            <h1 className="text-center text-[1.4rem] font-medium text-primary mb-2">
              Are you sure you want to approve this material request?
            </h1>
            <div className="bg-gray-100 h-px w-full"></div>
          </div>

          {/* Items List */}
          {approvals?.data?.find((item) => item.id === selectedApproval)?.dailyRequirementItems &&
            approvals.data.find((item) => item.id === selectedApproval).dailyRequirementItems
              .length > 0 && (
              <div className="w-full mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Items (
                  {
                    approvals.data.find((item) => item.id === selectedApproval)
                      .dailyRequirementItems.length
                  }
                  ):
                </h3>

                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {approvals.data
                    .find((item) => item.id === selectedApproval)
                    .dailyRequirementItems.map((item, index) => {
                      const usagePercentage =
                        item.estimatedQuantity && item.used
                          ? Math.round((item.used / item.estimatedQuantity) * 100)
                          : 0;

                      return (
                        <div
                          key={index}
                          className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                          {/* Item Header */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-[#346C75] rounded-full"></div>
                              <span className="text-sm font-semibold text-gray-800">
                                {item.item?.item_name || 'Unknown Item'}
                              </span>
                            </div>
                            <div className="px-2 py-1 bg-gray-100 rounded-full">
                              <span className="text-xs font-medium text-gray-600">
                                {item.status || 'Pending'}
                              </span>
                            </div>
                          </div>

                          {/* Item Details */}
                          <div className="grid grid-cols-2 gap-4 mb-3">
                            <div>
                              <span className="text-xs font-semibold text-gray-500 uppercase">
                                Category:
                              </span>
                              <p className="text-sm text-gray-800">
                                {item.item?.category?.name || item.item?.category_name || '--'}
                              </p>
                            </div>
                            <div>
                              <span className="text-xs font-semibold text-gray-500 uppercase">
                                Unit:
                              </span>
                              <p className="text-sm text-gray-800">{item.item?.unit || '--'}</p>
                            </div>
                          </div>

                          {/* Quantities Row */}
                          <div className="grid grid-cols-4 gap-2 mb-3">
                            <div className="text-center p-2 bg-gray-50 rounded">
                              <p className="text-xs font-semibold text-gray-500 uppercase">
                                Requested
                              </p>
                              <p className="text-sm font-bold text-gray-800">
                                {item.quantity || 0}
                              </p>
                            </div>
                            <div className="text-center p-2 bg-gray-50 rounded">
                              <p className="text-xs font-semibold text-gray-500 uppercase">
                                Estimated
                              </p>
                              <p className="text-sm font-bold text-green-600">
                                {item.estimatedQuantity || 0}
                              </p>
                            </div>
                            <div className="text-center p-2 bg-gray-50 rounded">
                              <p className="text-xs font-semibold text-gray-500 uppercase">Used</p>
                              <p className="text-sm font-bold text-red-600">{item.used || 0}</p>
                            </div>
                            <div className="text-center p-2 bg-gray-50 rounded">
                              <p className="text-xs font-semibold text-gray-500 uppercase">
                                Usage %
                              </p>
                              <p
                                className={`text-sm font-bold ${
                                  usagePercentage > 100
                                    ? 'text-red-600'
                                    : usagePercentage > 80
                                      ? 'text-yellow-600'
                                      : 'text-green-600'
                                }`}>
                                {usagePercentage}%
                              </p>
                            </div>
                          </div>

                          {/* Progress Bar */}
                          <div className="space-y-1">
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${
                                  usagePercentage > 100
                                    ? 'bg-red-500'
                                    : usagePercentage > 80
                                      ? 'bg-yellow-500'
                                      : 'bg-green-500'
                                }`}
                                style={{ width: `${Math.min(usagePercentage, 100)}%` }}></div>
                            </div>
                            <p className="text-xs text-gray-500 text-center">
                              {usagePercentage}% used
                            </p>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

          {/* Action Buttons */}
          <div className="w-full flex justify-end items-center space-x-4 mt-6 pt-4 border-t border-gray-200">
            <Button
              type="button"
              displayType="secondary"
              onClick={() => {
                setShowConfirmationModal(false);
                setSelectedApproval();
              }}
              disabled={isSubmitting}>
              Cancel
            </Button>
            <Button disabled={isSubmitting} type="button" onClick={handleApproval}>
              {isSubmitting ? 'Approving...' : 'Approve'}
            </Button>
          </div>
        </Modal>
      )}

      <div
        className={`fixed top-0 z-10 transition-all duration-300 bg-white ${
          collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'
        }`}>
        <Header
          data={{ name: `Operations Dashboard` }}
          backArrow={showHomeBack}
          onBack={showHomeBack ? onBack : undefined}
          showProjectSelector={false}>
          {showProgressionTab && (
            <ManagementHeaderTabs
              activeTab={headerTab}
              onTabChange={handleHeaderTabChange}
              tabCounts={hasProjectSelected ? { progression: progressionPendingCount } : undefined}
            />
          )}
        </Header>
        {isDashboardTab && (
        <DashboardFilters
          onFiltersChange={handleFiltersChange}
          onLoadingChange={handleFiltersLoadingChange}
          top="4rem"
          initialProjectID={location.state?.projectID}
          initialMonth={location.state?.month}
          projectCountsById={projectPriorityCountsById}
          rightContent={
            <nav
              className="inline-flex items-center gap-0.5 rounded-xl border border-gray-200 bg-gray-50 p-1 shadow-sm"
              aria-label="Operations reports">
              {[
                { label: 'Daily', path: '/builder/operations/daily-reports' },
                { label: 'Weekly', path: '/builder/operations/weekly-reports' },
                { label: 'Monthly', path: '/builder/operations/monthly-reports' }
              ].map((tab) => (
                <NavLink
                  key={tab.path}
                  to={tab.path}
                  state={{ projectID: filters.projectID, month: filters.month }}
                  className={({ isActive }) =>
                    [
                      'min-w-[5.5rem] rounded-lg px-4 py-2 text-center text-sm font-semibold transition-all duration-200',
                      isActive
                        ? 'bg-[#2A565E] text-white shadow-sm'
                        : 'text-[#346C75] hover:bg-white hover:text-[#2A565E] hover:shadow-sm'
                    ].join(' ')
                  }>
                  {tab.label}
                </NavLink>
              ))}
            </nav>
          }
        />
        )}
      </div>

      {showProgressionTab && headerTab === 'progression' && (
        <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
          <OperationsProgressionTab
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onFiltersLoadingChange={handleFiltersLoadingChange}
          />
        </div>
      )}

      {/* Body Content */}
      {isDashboardTab && (
      <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
        <div className="pt-[10rem] flex justify-flex-start items-start relative p-2 gap-7 pl-5">
          <div
            className={`flex flex-col gap-5 transition-all duration-300 ease-in-out ${
              collapsed ? 'w-[63%]' : 'w-[59%]'
            }`}>
            <AiPulsePanel projectId={filters.projectID} defaultVertical="operations" />
            <EngGraphs projectID={filters.projectID} month={filters.month} />
          </div>

          {/* Priorities and Approvals */}
          <div className="h-[75vh] overflow-hidden shadow flex flex-col bg-gray-100 w-[31%] fixed right-4">
            <div className="flex-1 overflow-y-auto rounded-lg bg-white p-4 shadow-lg">
              {/* Priorities Section */}
              <div className="text-center font-medium text-sm flex gap-3 justify-center items-center">
                <div className="relative date-picker-container">
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => handleDateChange(e.target.value)}
                    className="px-3 py-2 focus:outline-none text-sm font-medium text-[#346C75] bg-white transition-colors"
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
              <div className="mb-6 border border-gray-300 rounded-lg p-4">
                <h3 className="text-md font-semibold mb-4">Priorities</h3>
                {priorities &&
                  (() => {
                    const allItems = processPrioritiesData(priorities?.data);
                    const VISIBLE_LIMIT = 5;
                    const totalNonHeaders = allItems.filter((item) => item.type !== 'header').length;

                    let visibleItems = allItems;
                    if (!showAllPriorities && totalNonHeaders > VISIBLE_LIMIT) {
                      let count = 0;
                      let cutoffIndex = allItems.length;
                      for (let i = 0; i < allItems.length; i++) {
                        if (allItems[i].type !== 'header') {
                          count++;
                          if (count === VISIBLE_LIMIT) {
                            cutoffIndex = i + 1;
                            break;
                          }
                        }
                      }
                      visibleItems = allItems.slice(0, cutoffIndex);
                      if (visibleItems.length > 0 && visibleItems[visibleItems.length - 1].type === 'header') {
                        visibleItems = visibleItems.slice(0, -1);
                      }
                    }

                    return (
                      <>
                        {visibleItems.map((item, index) => {
                          if (item.type === 'header') {
                            return (
                              <p
                                key={`header-${index}`}
                                className="font-semibold text-sm text-gray-600 mb-2 mt-4">
                                {item.title}
                              </p>
                            );
                          }

                          if (item.type === 'mine') {
                            return (
                              <div
                                key={`mine-${item.taskType}-${index}`}
                                className="flex items-center justify-between border border-gray-200 rounded-lg p-2 cursor-pointer hover:bg-gray-50 mb-2"
                                onClick={async () => {
                                  const opsNavigation = getOperationsPriorityNavigation(item.taskType);

                                  if (opsNavigation) {
                                    if (shouldAutoCompleteOperationsPriorityOnRedirect(item.taskType)) {
                                      try {
                                        const pendingTasks = getPendingOperationsDailyTasks(
                                          priorities?.data,
                                          item.taskType,
                                          userID
                                        );
                                        await Promise.all(
                                          pendingTasks.map((task) =>
                                            updateTaskForTomorrow(task.id, { status: 'Completed' })
                                          )
                                        );
                                        if (pendingTasks.length > 0) {
                                          await refetchPriorities();
                                        }
                                      } catch (error) {
                                        toast.error(
                                          error?.response?.data?.message ||
                                            error?.message ||
                                            'Failed to complete task'
                                        );
                                      }
                                    }

                                    navigateWithReturn(navigate, opsNavigation.path, {
                                      returnTo: buildReturnToFromLocation(location),
                                      state: {
                                        activeTab: opsNavigation.taskManagementTab,
                                        templateTab: opsNavigation.templateTab,
                                        projectID: filters.projectID
                                      }
                                    });
                                  } else {
                                    const route =
                                      item.taskType === 'Management Task'
                                        ? '/builder/operations/managementTasks'
                                        : '/builder/operations/tasksUpdates';

                                    navigateWithReturn(navigate, route, {
                                      returnTo: buildReturnToFromLocation(location),
                                      state: {
                                        taskType: item.taskType,
                                        userID: item.userId,
                                        projectID: filters.projectID,
                                        selectedDate:
                                          localStorage.getItem('engineerSelectedDate') ||
                                          selectedDate,
                                        currentUserId: userID
                                      }
                                    });
                                  }

                                  mixpanel.track('Priority Item Clicked', {
                                    user_id: userID,
                                    priority_type: 'mine',
                                    task_type: item.taskType,
                                    count: item.count,
                                    project_id: filters.projectID,
                                    destination: opsNavigation?.path ?? undefined,
                                    template_tab: opsNavigation?.templateTab ?? undefined,
                                    timestamp: new Date().toISOString()
                                  });
                                }}>
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-semibold">
                                    {item.userName.charAt(0).toUpperCase()}
                                  </div>
                                  <span className="text-sm font-medium text-gray-800">
                                    {item.userName} | {item.taskType} | {item.count}
                                  </span>
                                </div>
                              </div>
                            );
                          }

                          if (item.type === 'others') {
                            return (
                              <div
                                key={`others-${item.userId}-${index}`}
                                className="flex items-center justify-between border border-gray-200 rounded-lg p-2 cursor-pointer hover:bg-gray-50 mb-2"
                                onClick={() => {
                                  navigateWithReturn(
                                    navigate,
                                    `/builder/operations/tasksUpdates`,
                                    {
                                      returnTo: buildReturnToFromLocation(location),
                                      state: {
                                        taskType: 'Escalated',
                                        userID: item.userId,
                                        projectID: filters.projectID,
                                        selectedDate:
                                          localStorage.getItem('engineerSelectedDate') ||
                                          selectedDate
                                      }
                                    }
                                  );
                                  mixpanel.track('Priority Item Clicked', {
                                    user_id: userID,
                                    priority_type: 'others',
                                    target_user_id: item.userId,
                                    target_user_name: item.userName,
                                    escalated_count: item.escalatedCount,
                                    total_count: item.totalCount,
                                    project_id: filters.projectID,
                                    timestamp: new Date().toISOString()
                                  });
                                }}>
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 rounded-full bg-orange-500 text-white flex items-center justify-center text-xs font-semibold">
                                    {item.userName.charAt(0).toUpperCase()}
                                  </div>
                                  <span className="text-sm font-medium text-gray-800">
                                    {item.userName} | Escalated | {item.escalatedCount}
                                  </span>
                                </div>
                              </div>
                            );
                          }

                          return null;
                        })}
                        {totalNonHeaders > VISIBLE_LIMIT && (
                          <button
                            onClick={() => setShowAllPriorities(!showAllPriorities)}
                            className="w-full mt-2 py-2 text-sm font-medium text-[#346C75] hover:text-[#2a565d] hover:bg-gray-50 rounded-lg transition-colors">
                            {showAllPriorities
                              ? 'View Less'
                              : `View More (${totalNonHeaders - VISIBLE_LIMIT} more)`}
                          </button>
                        )}
                      </>
                    );
                  })()}
              </div>

              {/* Pending Approvals Section */}
              <div className="border border-gray-300 rounded-lg p-4">
                <h3 className="text-md font-semibold mb-4">Pending Approvals</h3>
                {(() => {
                  const APPROVALS_LIMIT = 4;
                  const pendingApprovals = approvals?.data?.filter((item) => !item.approved) || [];

                  if (pendingApprovals.length === 0) {
                    return (
                      <div className="text-center py-4">
                        <p className="text-sm text-gray-500">No pending approvals</p>
                      </div>
                    );
                  }

                  const visibleApprovals = showAllApprovals
                    ? pendingApprovals
                    : pendingApprovals.slice(0, APPROVALS_LIMIT);

                  return (
                    <>
                      <div className="space-y-3">
                        {visibleApprovals.map((item) => (
                          <div
                            className="border border-gray-200 rounded-lg p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                            key={item.id}
                            onClick={() => {
                              mixpanel.track('Approval Item Clicked', {
                                user_id: userID,
                                activity_id: item.id,
                                target_user_id: item?.user?.id,
                                target_user_name: item?.user?.name,
                                project_id: filters.projectID,
                                timestamp: new Date().toISOString()
                              });

                              setShowConfirmationModal(true);
                              setSelectedApproval(item.id);
                            }}>
                            {/* User Info */}
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-semibold">
                                {item?.user?.name?.charAt(0)?.toUpperCase()}
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-gray-800">
                                  {item?.user?.name}
                                </p>
                                <p className="text-xs text-gray-500">{item?.user?.email}</p>
                              </div>
                            </div>

                            {/* Task Details */}
                            <div className="mb-2">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-gray-700">Task:</span>
                                <span className="text-sm text-gray-800">{item?.task?.name}</span>
                              </div>
                            </div>

                            {/* Item Details */}
                            <div className="mb-2 flex flex-row justify-between items-start w-full">
                              <div className="flex flex-row gap-1 justify-between  w-full">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-bold text-gray-700">Item:</span>
                                  <span className="text-sm text-gray-800">
                                    {item?.dailyRequirementItems?.length || 0}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-bold text-gray-700">Due Date:</span>
                                  <span className="text-xs text-gray-600">
                                    {new Date(item?.task?.planned_end_date).toLocaleDateString()}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {pendingApprovals.length > APPROVALS_LIMIT && (
                        <button
                          onClick={() => setShowAllApprovals(!showAllApprovals)}
                          className="w-full mt-3 py-2 text-sm font-medium text-[#346C75] hover:text-[#2a565d] hover:bg-gray-50 rounded-lg transition-colors">
                          {showAllApprovals
                            ? 'View Less'
                            : `View More (${pendingApprovals.length - APPROVALS_LIMIT} more)`}
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Change Requests Section */}
              <div className="border border-gray-300 rounded-lg p-4 mt-4">
                <h3 className="text-md font-semibold mb-4">Change Requests</h3>
                {(() => {
                  const CR_LIMIT = 4;
                  const crList = changeRequests?.data || [];

                  if (crList.length === 0) {
                    return (
                      <div className="text-center py-4">
                        <p className="text-sm text-gray-500">No change requests</p>
                      </div>
                    );
                  }

                  const visibleCRs = showAllChangeRequests
                    ? crList
                    : crList.slice(0, CR_LIMIT);

                  return (
                    <>
                      <div className="space-y-3">
                        {visibleCRs.map((item) => {
                          const statusColor =
                            item.globalStatus === 'Accepted'
                              ? 'bg-green-100 text-green-700'
                              : item.globalStatus === 'Rejected'
                                ? 'bg-red-100 text-red-700'
                                : item.globalStatus === 'Approved'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-yellow-100 text-yellow-700';

                          return (
                            <div
                              className="border border-gray-200 rounded-lg p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                              key={item.id}
                              onClick={() => {
                                mixpanel.track('Change Request Clicked from Dashboard', {
                                  user_id: userID,
                                  change_request_id: item.id,
                                  project_id: filters.projectID,
                                  timestamp: new Date().toISOString()
                                });
                                navigate('/builder/operations/change-requests');
                              }}>
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center text-xs font-semibold">
                                  {item.deal?.lead?.user?.name?.charAt(0)?.toUpperCase() || 'C'}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-gray-800 truncate">
                                    {item.deal?.lead?.user?.name || 'N/A'}
                                  </p>
                                  <p className="text-xs text-gray-500 truncate">
                                    {item.deal?.unit?.project?.name || 'N/A'}
                                  </p>
                                </div>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>
                                  {item.globalStatus || 'Pending'}
                                </span>
                              </div>

                              <div className="flex flex-row justify-between items-center w-full">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-bold text-gray-700">Unit:</span>
                                  <span className="text-xs text-gray-600">
                                    {item.deal?.unit?.unitName || 'N/A'}/{item.deal?.unit?.block?.name || 'N/A'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-bold text-gray-700">Requests:</span>
                                  <span className="text-xs text-gray-600">
                                    {item.total_requirements || 0}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {crList.length > CR_LIMIT && (
                        <button
                          onClick={() => setShowAllChangeRequests(!showAllChangeRequests)}
                          className="w-full mt-3 py-2 text-sm font-medium text-[#346C75] hover:text-[#2a565d] hover:bg-gray-50 rounded-lg transition-colors">
                          {showAllChangeRequests
                            ? 'View Less'
                            : `View More (${crList.length - CR_LIMIT} more)`}
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>
      )}
    </Layout>
  );
};

export default EngDashboard;
